/**
 * taskflow-query-parser.ts
 *
 * Tokenized Bounded Query Language and AST Compiler for TaskFlow.
 * Supports token filters (status:, priority:, type:, parent:, assignee:, due:),
 * quoted multi-word strings, tags (@tag, context:), and boolean negation (-status:done).
 */

import type { TaskFlowItem } from '../contracts/taskflow-schema.js';

export interface TaskFlowQueryFilter {
  readonly isEmpty: boolean;
  readonly status: string[];
  readonly excludedStatus: string[];
  readonly priority: string[];
  readonly excludedPriority: string[];
  readonly type: string[];
  readonly excludedType: string[];
  readonly tags: string[];
  readonly excludedTags: string[];
  readonly parent?: string;
  readonly assignee?: string;
  readonly isOverdue?: boolean;
  readonly dueDate?: string;
  readonly titleMatch?: string;
  readonly searchKeyword?: string;
}

/**
 * Tokenize input query string respecting quoted strings (single and double quotes)
 */
function tokenizeQuery(raw: string): string[] {
  const tokens: string[] = [];
  const regex = /(-?[a-zA-Z0-9_@]+:(?:"[^"]*"|'[^']*'|[^\s]+)|"[^"]*"|'[^']*'|[^\s]+)/g;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(raw)) !== null) {
    tokens.push(match[0]);
  }

  return tokens;
}

/**
 * Parse a raw query string into a structured TaskFlowQueryFilter
 */
export function parseTaskFlowQuery(queryString: string): TaskFlowQueryFilter {
  const trimmed = (queryString || '').trim();
  if (!trimmed) {
    return {
      isEmpty: true,
      status: [],
      excludedStatus: [],
      priority: [],
      excludedPriority: [],
      type: [],
      excludedType: [],
      tags: [],
      excludedTags: [],
    };
  }

  const tokens = tokenizeQuery(trimmed);
  const status: string[] = [];
  const excludedStatus: string[] = [];
  const priority: string[] = [];
  const excludedPriority: string[] = [];
  const type: string[] = [];
  const excludedType: string[] = [];
  const tags: string[] = [];
  const excludedTags: string[] = [];
  const freeKeywords: string[] = [];

  let parent: string | undefined;
  let assignee: string | undefined;
  let isOverdue: boolean | undefined;
  let dueDate: string | undefined;
  let titleMatch: string | undefined;

  for (const rawToken of tokens) {
    const isNegated = rawToken.startsWith('-');
    const token = isNegated ? rawToken.slice(1) : rawToken;

    if (token.toLowerCase().startsWith('status:')) {
      let val = token.slice(7).trim();
      val = val.replace(/^["']|["']$/g, '').toLowerCase();
      if (val) {
        if (isNegated) excludedStatus.push(val);
        else status.push(val);
      }
    } else if (token.toLowerCase().startsWith('priority:')) {
      let val = token.slice(9).trim();
      val = val.replace(/^["']|["']$/g, '').toLowerCase();
      if (val) {
        if (isNegated) excludedPriority.push(val);
        else priority.push(val);
      }
    } else if (token.toLowerCase().startsWith('type:')) {
      let val = token.slice(5).trim();
      val = val.replace(/^["']|["']$/g, '').toLowerCase();
      if (val) {
        if (isNegated) excludedType.push(val);
        else type.push(val);
      }
    } else if (token.startsWith('@') || token.toLowerCase().startsWith('tag:') || token.toLowerCase().startsWith('context:')) {
      let val = token;
      if (val.startsWith('@')) val = val.slice(1);
      else if (val.toLowerCase().startsWith('tag:')) val = val.slice(4);
      else if (val.toLowerCase().startsWith('context:')) val = val.slice(8);
      val = val.replace(/^["']|["']$/g, '').toLowerCase();
      if (val) {
        if (isNegated) excludedTags.push(val);
        else tags.push(val);
      }
    } else if (token.toLowerCase().startsWith('title:')) {
      let val = token.slice(6).trim();
      val = val.replace(/^["']|["']$/g, '');
      if (val) {
        titleMatch = val;
      }
    } else if (token.toLowerCase().startsWith('parent:')) {
      let val = token.slice(7).trim();
      val = val.replace(/^["']|["']$/g, '');
      if (val) {
        parent = val;
      }
    } else if (token.toLowerCase().startsWith('assignee:')) {
      let val = token.slice(9).trim();
      val = val.replace(/^["']|["']$/g, '');
      if (val) {
        assignee = val;
      }
    } else if (token.toLowerCase() === 'due:overdue' || token.toLowerCase() === 'is:overdue') {
      isOverdue = true;
    } else if (token.toLowerCase().startsWith('due:')) {
      const val = token.slice(4).trim().replace(/^["']|["']$/g, '');
      if (val.toLowerCase() === 'overdue') {
        isOverdue = true;
      } else {
        dueDate = val;
      }
    } else {
      // Free text search keyword
      const cleaned = rawToken.replace(/^["']|["']$/g, '');
      if (cleaned) {
        freeKeywords.push(cleaned);
      }
    }
  }

  return {
    isEmpty: false,
    status,
    excludedStatus,
    priority,
    excludedPriority,
    type,
    excludedType,
    tags,
    excludedTags,
    parent,
    assignee,
    isOverdue,
    dueDate,
    titleMatch,
    searchKeyword: freeKeywords.length > 0 ? freeKeywords.join(' ') : undefined,
  };
}

/**
 * Match a TaskFlowItem against a compiled TaskFlowQueryFilter
 */
export function matchTaskFlowItem(item: TaskFlowItem, filter: TaskFlowQueryFilter): boolean {
  if (filter.isEmpty) return true;

  // 1. Status inclusion/exclusion
  const itemStatus = (item.status || '').toLowerCase();
  if (filter.status.length > 0 && !filter.status.includes(itemStatus)) {
    return false;
  }
  if (filter.excludedStatus.length > 0 && filter.excludedStatus.includes(itemStatus)) {
    return false;
  }

  // 2. Priority inclusion/exclusion
  const itemPriority = (item.priority || 'normal').toLowerCase();
  if (filter.priority.length > 0 && !filter.priority.includes(itemPriority)) {
    return false;
  }
  if (filter.excludedPriority.length > 0 && filter.excludedPriority.includes(itemPriority)) {
    return false;
  }

  // 3. Type inclusion/exclusion
  const itemType = (item.type || '').toLowerCase();
  if (filter.type.length > 0 && !filter.type.includes(itemType)) {
    return false;
  }
  if (filter.excludedType.length > 0 && filter.excludedType.includes(itemType)) {
    return false;
  }

  // 4. Tags inclusion/exclusion
  const itemTags = (item.tags || []).map((t) => t.toLowerCase());
  for (const reqTag of filter.tags) {
    if (!itemTags.includes(reqTag)) return false;
  }
  for (const exclTag of filter.excludedTags) {
    if (itemTags.includes(exclTag)) return false;
  }

  // 5. Parent match
  if (filter.parent) {
    if (item.parent !== filter.parent) return false;
  }

  // 6. Assignee match
  if (filter.assignee) {
    const assignees = item.assigneeIds || [];
    if (!assignees.includes(filter.assignee) && filter.assignee !== '@me') {
      return false;
    }
  }

  // 7. Title exact match substring
  if (filter.titleMatch) {
    if (!item.title.toLowerCase().includes(filter.titleMatch.toLowerCase())) {
      return false;
    }
  }

  // 8. Due date & Overdue check
  if (filter.isOverdue) {
    if (!item.dueDate) return false;
    const dueTime = new Date(item.dueDate).getTime();
    if (isNaN(dueTime) || dueTime >= Date.now() || item.status === 'done') {
      return false;
    }
  }
  if (filter.dueDate) {
    if (!item.dueDate || !item.dueDate.startsWith(filter.dueDate)) {
      return false;
    }
  }

  // 9. Free keyword search
  if (filter.searchKeyword) {
    const kw = filter.searchKeyword.toLowerCase();
    const inTitle = item.title.toLowerCase().includes(kw);
    const inDesc = (item.description || '').toLowerCase().includes(kw);
    const inTags = (item.tags || []).some((t) => t.toLowerCase().includes(kw));
    if (!inTitle && !inDesc && !inTags) {
      return false;
    }
  }

  return true;
}
